import { LanguageDirection } from "@snapchat/content-platform-www/generated-src/snapchat/web/stories/common";
import { pageLinksOrDefault, pageMetadataOrDefault, viewerInfoOrDefault, } from "@snapchat/web-spotlight-conversions";
import { PageType } from "@snapchat/web-blizzard-reporting";
import { useWebSession } from "@snapchat/web-blizzard-reporting-hooks";
import { ConsumerApp } from "@snapchat/web-consumer-nav";
import { MobileCTAVariants, PageFrame, Shell } from "@snapchat/web-frame";
import { buildVideoObjectLD } from "@snapchat/web-head-meta";
import { getDirectionForLocale } from "@snapchat/web-i18n";
import { useGenerateWebPageViewTraceId } from "@snapchat/web-page-view";
import { usePageLoadMetrics } from "@snapchat/web-performance-metrics";
import { addWebClientIdToOneLink, useCreateOrUpdateWebClientId, useEssentialSession, generateDeeplinkOrWebFallbackUrl, } from "@snapchat/web-sc-cookies";
import React, { useMemo, useCallback } from "react";
import { grafanaBasePrefix } from "../../../lib/grafana";
import { SpotlightForLensesOption } from "../../common/Shell/webConfigContext";
import styles from "./LensProfile.module.css";
import dynamic from "next/dynamic";
import { getBaseShellProps } from "../../../lib/shell";
import { MobileLensProfile } from "./MobileLensProfile";
import { ExperimentId } from "../../../lib/experiment";
import { useIntl } from "react-intl";
import { lensDisplayInfoOrDefault } from "../../../types/conversions";
import { WWW_DOT_SNAPCHAT_DOT_COM_GA } from "@snapchat/web-analytics-types";
import { getNavigator } from "@snapchat/web-env";
const pageName = "Unlock";
export function getDeepLinkUrl(scannableUuid, oneLinkUrl) {
    const navigatorObj = getNavigator();
    // we have an issue where the Snapchat app is not recognizing lens.snapchat.com as a
    // deeplink URL - this causes lens links to open the webview and render this page;
    // the "Open in Snapchat" link subsequently links the user to the download page rather than
    // deeplinking to the lens. Workaround this for now, by trying to detect if we're inside
    // a snapchat webview; if so, rewrite the OneLink URL so we just link directly to the
    // deeplink URL that the app understands.
    /* istanbul ignore next : Remove once test code is added */
    if (typeof navigatorObj !== "undefined" &&
        navigatorObj.userAgent.toLowerCase().includes("snapchat")) {
        return `https://link.snapchat.com/unlock/?type=SNAPCODE&uuid=${scannableUuid}&metadata=01`;
    }
    return oneLinkUrl;
}
const DesktopLensProfile = dynamic(async () => 
// https://nextjs.org/docs/advanced-features/dynamic-import#with-named-exports
// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-return
import("./DesktopLensProfile").then((module) => module.DesktopLensProfile));
export function LensProfile(props) {
    const { traceId: webPageViewTraceId } = useGenerateWebPageViewTraceId();
    const webClientId = useCreateOrUpdateWebClientId({
        isGdpr: props.viewerInfo?.isGdprCountry,
    });
    const locale = viewerInfoOrDefault(props.viewerInfo).locale;
    const pageType = PageType.LENS_PROFILE;
    const basePageProps = {
        ...props,
        viewerInfo: viewerInfoOrDefault(props.viewerInfo),
        localization: props.localization ||
            {
                direction: LanguageDirection.AUTO,
            },
        lensDisplayInfo: lensDisplayInfoOrDefault(props.lensDisplayInfo),
        pageLinks: pageLinksOrDefault(props.pageLinks),
        pageMetadata: pageMetadataOrDefault(props.pageMetadata),
        pageName,
        locale,
    };
    useEssentialSession();
    const { pageMetadata, lensDisplayInfo, pageLinks, linkPreview, viewerInfo } = basePageProps;
    const openInSnapchatLink = getDeepLinkUrl(lensDisplayInfo.scannableUuid, pageLinks.oneLinkUrl);
    const snapchatOneLink = addWebClientIdToOneLink(openInSnapchatLink, webClientId);
    const propsWithDefaults = {
        ...basePageProps,
        openInSnapchatLink: snapchatOneLink,
    };
    const webVitalsRef = usePageLoadMetrics(PageType[pageType], grafanaBasePrefix);
    useWebSession(pageType, webVitalsRef, webClientId);
    const preloads = [
        { href: lensDisplayInfo.lensPreviewImageUrl, as: "image" },
        { href: lensDisplayInfo.lensPreviewVideoUrl, as: "video" },
    ];
    const baseShellProps = useMemo(() => getBaseShellProps(), []);
    const footerProps = {
        locale: locale,
        openInSnapchatLink: snapchatOneLink,
        downloadUrl: pageLinks.downloadUrl,
        oneLinkParams: pageLinks.oneLinkParams,
        localeChange: props.changeLocale,
        webClientId: webClientId,
        webPageViewTraceId: webPageViewTraceId,
        pageType: pageMetadata.pageType,
        mobileVariant: MobileCTAVariants.EXPANDED_TRAY_WITH_OVERLAY,
    };
    const intl = useIntl();
    const newOISExperimentProps = {
        enableNewOISExperiment: true,
        configID: ExperimentId.NEW_MOBILE_OIS_CTA_DESIGN,
        newFooterProps: {
            prefixText: intl.formatMessage({
                description: "Footer Text for Open in Snapchat app button",
                defaultMessage: "Try out the {lensName} Lens in the app",
            }, {
                lensName: lensDisplayInfo.lensName,
            }),
            prefixImgUrl: lensDisplayInfo.iconUrl,
            webClientId: webClientId ?? "",
            webPageViewTraceId,
            pageType,
            attribution: {
                oneLinkParams: pageLinks.oneLinkParams,
                openInSnapchatLink: pageLinks.oneLinkUrl,
                downloadLink: pageLinks.downloadUrl,
            },
        },
    };
    // Build a single VideoObject to represent the lens video
    const linkedData = props.lensMetadata ? buildVideoObjectLD(props.lensMetadata) : undefined;
    return (<Shell isGDPRCountry={propsWithDefaults.viewerInfo.isGdprCountry} locale={locale} messages={props.messages} country={viewerInfo.country} mountByDefault canonicalUrl={pageLinks.canonicalUrl} hrefLangTagOptions={{
            template: `${pageLinks.canonicalUrl}?locale={locale}`,
            defaultValue: pageLinks.canonicalUrl,
        }} direction={getDirectionForLocale(locale)} description={pageMetadata.pageDescription?.value} snapchatDeepLinkUrl={pageLinks.snapchatDeepLinkUrl} linkPreview={linkPreview} title={pageMetadata.pageTitle} pageMetadata={{ pageType }} webClientId={webClientId} gaid={propsWithDefaults.viewerInfo.isGdprCountry ? undefined : WWW_DOT_SNAPCHAT_DOT_COM_GA} preloads={preloads} webPageViewTraceId={webPageViewTraceId} skipWebConfigNamespaceProviderInitialization={basePageProps.skipWebConfigNamespaceProviderInitialization} linkedData={linkedData} {...baseShellProps}>
      <div className={styles.pageWrapper}>
        <PageFrame locale={locale} localeChange={props.changeLocale} appType={ConsumerApp.Lens} pageType={pageMetadata.pageType} downloadUrl={pageLinks.downloadUrl} webClientId={webClientId} webPageViewTraceId={webPageViewTraceId} footerProps={footerProps} newOISExperimentProps={newOISExperimentProps} disableUpsellQRCode={props.disableUpsellQRCode}>
          <div className={styles.container}>
            <div className="mobileOnly">
              <MobileLensProfileWithExperiment {...propsWithDefaults} webClientId={webClientId || ""}/>
            </div>
            <div className="desktopOnly">
              <DesktopLensProfile {...propsWithDefaults} spotlightOption={SpotlightForLensesOption.spotlightsFirst} pageName={pageName} locale={locale}/>
            </div>
          </div>
        </PageFrame>
      </div>
    </Shell>);
}
function MobileLensProfileWithExperiment(props) {
    const { webClientId } = props;
    const makeClickLink = useCallback((url) => generateDeeplinkOrWebFallbackUrl(url, pageName, webClientId), [webClientId]);
    // Turn spotlight links into click links
    const adaptedSpotlightsUsingThisLens = useMemo(() => props.spotlightsUsingThisLens.map((s) => ({
        ...s,
        storyPageUrl: makeClickLink(s.storyPageUrl),
    })), [props.spotlightsUsingThisLens, makeClickLink]);
    return (<MobileLensProfile {...props} spotlightsUsingThisLens={adaptedSpotlightsUsingThisLens} spotlightOption={SpotlightForLensesOption.spotlightsFirst}/>);
}
